import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import useAuthStore from '../context/authStore';
import toast from 'react-hot-toast';
import { getReels, getSubjects, uploadReel, likeReel, saveReel, viewReel, deleteReel } from '../services/api';
import './ReelsPage.css';

/* ─── Single Reel Card ─── */
function ReelCard({ reel, isActive, onDelete }) {
  const videoRef = useRef();
  const { user, userDoc } = useAuthStore();
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [likes, setLikes] = useState(reel.likes?.length || 0);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (user) {
      setLiked(reel.likes?.includes(user.uid) || false);
      setSaved(reel.saves?.includes(user.uid) || false);
    }
  }, [user, reel]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isActive) {
      v.play().catch(() => {});
      viewReel(reel.id).catch(() => {});
    } else {
      v.pause();
      v.currentTime = 0;
    }
  }, [isActive, reel.id]);

  const handleLike = async () => {
    if (!user) return toast.error('سجّل دخولك أولاً');
    const next = !liked;
    setLiked(next);
    setLikes(l => next ? l + 1 : l - 1);
    try { await likeReel(reel.id); }
    catch { setLiked(!next); setLikes(l => next ? l - 1 : l + 1); }
  };

  const handleSave = async () => {
    if (!user) return toast.error('سجّل دخولك أولاً');
    const next = !saved;
    setSaved(next);
    try { await saveReel(reel.id); toast.success(next ? 'تم الحفظ ✅' : 'تم إلغاء الحفظ'); }
    catch { setSaved(!next); }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: reel.title, url }).catch(() => {});
    else { navigator.clipboard.writeText(url); toast.success('تم نسخ الرابط!'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('حذف هذا الريل نهائياً؟')) return;
    try { await deleteReel(reel.id); toast.success('تم الحذف'); onDelete?.(reel.id); }
    catch { toast.error('فشل الحذف'); }
  };

  const isOwner = user?.uid === reel.teacherId || userDoc?.role === 'admin';

  return (
    <div className="reel-card">
      <video
        ref={videoRef}
        src={reel.videoUrl}
        loop
        muted={muted}
        playsInline
        controlsList="nodownload"
        onContextMenu={e => e.preventDefault()}
        onClick={() => setMuted(m => !m)}
        className="reel-video"
      />
      {muted && (
        <button className="reel-muted-hint" onClick={() => setMuted(false)}>
          🔇 انقر للصوت
        </button>
      )}

      <div className="reel-overlay">
        <div className="reel-bottom">
          <div className="reel-info">
            <Link to={`/teachers/${reel.teacherUsername || reel.teacherId}`} className="reel-teacher-row">
              {reel.teacherAvatar
                ? <img src={reel.teacherAvatar} alt="" className="reel-teacher-avatar" />
                : <div className="reel-teacher-avatar reel-teacher-ph">{reel.teacherName?.[0]}</div>
              }
              <span>{reel.teacherName}</span>
            </Link>
            <h3 className="reel-title">{reel.title}</h3>
            {reel.description && <p className="reel-desc">{reel.description}</p>}
            <div className="reel-tags">
              <span className="reel-tag">{reel.subject}</span>
              {reel.courseId && (
                <Link to={`/courses/${reel.courseSlug || reel.courseId}`} className="reel-tag reel-tag-course">
                  الكورس ←
                </Link>
              )}
            </div>
          </div>

          <div className="reel-actions">
            <button className={`reel-btn ${liked ? 'reel-btn-liked' : ''}`} onClick={handleLike}>
              <span className="reel-btn-icon">{liked ? '❤️' : '🤍'}</span>
              <span>{likes}</span>
            </button>
            <button className={`reel-btn ${saved ? 'reel-btn-saved' : ''}`} onClick={handleSave}>
              <span className="reel-btn-icon">{saved ? '🔖' : '📌'}</span>
              <span>حفظ</span>
            </button>
            <button className="reel-btn" onClick={handleShare}>
              <span className="reel-btn-icon">↗</span>
              <span>شارك</span>
            </button>
            {isOwner && (
              <button className="reel-btn reel-btn-del" onClick={handleDelete}>
                <span className="reel-btn-icon">🗑️</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Upload Form (Teacher) ─── */
function UploadReelForm({ onUploaded }) {
  const [form, setForm] = useState({ title: '', description: '', subject: '', courseId: '' });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('اختر فيديو أولاً');
    if (!form.title || !form.subject) return toast.error('العنوان والمادة مطلوبان');
    setUploading(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append('video', file);
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      await uploadReel(fd, setProgress);
      toast.success('تم رفع الريل بنجاح! 🎬');
      setFile(null);
      setForm({ title: '', description: '', subject: '', courseId: '' });
      onUploaded?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'فشل الرفع');
    } finally { setUploading(false); setProgress(0); }
  };

  return (
    <form onSubmit={handleSubmit} className="reel-upload-form">
      <h3>رفع ريل جديد 🎬</h3>

      <label className="reel-file-label">
        {file ? `📹 ${file.name}` : '📹 اختر فيديو (حتى 150MB)'}
        <input type="file" accept="video/*" onChange={e => setFile(e.target.files[0])} style={{ display: 'none' }} />
      </label>

      {uploading && (
        <div className="reel-progress-wrap">
          <div className="reel-progress-bar" style={{ width: `${progress}%` }} />
          <span>{progress}%</span>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">عنوان الريل *</label>
        <input className="form-input" placeholder="مثال: حل المعادلات في دقيقتين"
          value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required />
      </div>
      <div className="form-group">
        <label className="form-label">المادة *</label>
        <input className="form-input" placeholder="مثال: رياضيات"
          value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} required />
      </div>
      <div className="form-group">
        <label className="form-label">وصف مختصر</label>
        <textarea className="form-input" rows={2} placeholder="اشرح محتوى الريل..."
          value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
      </div>
      <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={uploading}>
        {uploading ? `جارٍ الرفع ${progress}%...` : 'نشر الريل'}
      </button>
    </form>
  );
}

/* ─── Main Page ─── */
export default function ReelsPage() {
  const [reels, setReels]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [subjects, setSubjects]   = useState([]);
  const [filter, setFilter]       = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const { user, userDoc } = useAuthStore();
  const observerRef = useRef();

  const isTeacher = ['teacher', 'admin'].includes(userDoc?.role);

  useEffect(() => {
    getSubjects().then(r => setSubjects(r.data?.subjects || [])).catch(() => {});
  }, []);

  const loadReels = useCallback((subjectFilter = '') => {
    setLoading(true);
    const params = {};
    if (subjectFilter) params.subject = subjectFilter;
    getReels(params)
      .then(r => { setReels(r.data || []); setActiveIdx(0); })
      .catch(() => setReels([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadReels(filter); }, [filter, loadReels]);

  /* Intersection observer — detects which card is centred */
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting)
            setActiveIdx(parseInt(entry.target.dataset.idx, 10));
        });
      },
      { threshold: 0.65 }
    );
    return () => observerRef.current?.disconnect();
  }, []);

  const cardRef = useCallback(el => {
    if (el && observerRef.current) observerRef.current.observe(el);
  }, []);

  const handleDelete = id => setReels(prev => prev.filter(r => r.id !== id));

  return (
    <div className="reels-page">
      {/* Sidebar */}
      <aside className="reels-sidebar">
        <h2 className="reels-sidebar-title">🎬 ريلز تعليمي</h2>
        <p className="reels-sidebar-sub">فيديوهات قصيرة من أفضل المدرسين</p>

        <div className="form-group" style={{ marginTop: 16 }}>
          <label className="form-label">فلتر المادة</label>
          <select className="form-input" value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="">كل المواد</option>
            {subjects.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {isTeacher && (
          <button
            className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
            onClick={() => setShowUpload(v => !v)}
          >
            {showUpload ? '✕ إلغاء' : '+ رفع ريل جديد'}
          </button>
        )}

        {isTeacher && showUpload && (
          <UploadReelForm onUploaded={() => { setShowUpload(false); loadReels(filter); }} />
        )}

        {!user && (
          <div className="reels-login-prompt">
            <p>سجّل دخولك للايك وحفظ الريلز</p>
            <Link to="/login" className="btn btn-outline btn-sm" style={{ width: '100%', justifyContent: 'center' }}>
              تسجيل الدخول
            </Link>
          </div>
        )}
      </aside>

      {/* Feed */}
      <div className="reels-feed">
        {loading ? (
          <div className="reel-card reel-state-card">
            <div className="spinner" />
          </div>
        ) : reels.length === 0 ? (
          <div className="reel-card reel-state-card">
            <span style={{ fontSize: 60 }}>🎬</span>
            <h3>لا توجد ريلز بعد</h3>
            {isTeacher && <p>كن أول من يرفع ريل تعليمي!</p>}
          </div>
        ) : (
          reels.map((reel, idx) => (
            <div key={reel.id} data-idx={idx} ref={cardRef} className="reel-snap-item">
              <ReelCard reel={reel} isActive={idx === activeIdx} onDelete={handleDelete} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
